import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { DateRange, DateRangePicker } from "@mui/x-date-pickers-pro";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../app/constants";
import FormSaveCancelButtons from "../../../common/components/functional/formSaveCancelButtons/FormSaveCancelButtons";
import {
  dateTimeFormat,
  isoFormat,
} from "../../../common/helpers/formatDateTime";
import { SchedulingPeriod } from "../settingsApi";
import { HeaderBox, TableCellConstantWidth, TableCellGray } from "./styled";

interface Props {
  type: string; // scheduling class type
  isNewSchedulingPeriod: boolean;
  row: SchedulingPeriod;
  onClose: () => void;
  onSave: (row: SchedulingPeriod) => void;
}

export const SchedulingPeriodView = ({
  type,
  row,
  onClose,
  onSave,
  isNewSchedulingPeriod,
}: Props) => {
  const { t } = useTranslation("schedulingPeriods");
  const [schedule, setSchedule] = useState(row);
  const [scheduleDateRange, setScheduleDateRange] = useState<DateRange<Date>>([
    new Date(`${schedule.start}T00:00:00`),
    new Date(`${schedule.end}T23:59:00`),
  ]);
  const [isFormValid, setIsFormValid] = useState(true);

  const updateDateRange = (newValue: DateRange<Date>) => {
    if (!!newValue[0] && !!newValue[1]) {
      const newStartDate = format(newValue[0], isoFormat);
      const newEndDate = format(newValue[1], isoFormat);
      setScheduleDateRange(newValue);
      setSchedule({ ...schedule, start: newStartDate, end: newEndDate });
    }
  };

  const handleScheduleSet = (
    date: Date | null,
    toEpoch: boolean,
    propertyName: string,
  ) => {
    if (date) {
      const formatted = toEpoch ? date.getTime() : format(date, isoFormat);
      setSchedule({ ...schedule, [propertyName]: formatted });
    }
  };
  const updateSalaryDate = (date: Date | null) => {
    handleScheduleSet(date, false, "salaryDate");
  };

  const updateCustomerApproval = (date: Date | null) => {
    handleScheduleSet(date, true, "entriesApprovedByCustomer");
  };

  const updateSystemApproval = (date: Date | null) => {
    handleScheduleSet(date, true, "entriesApprovedBySystem");
  };

  const updateInvoicesWave1 = (date: Date | null) => {
    handleScheduleSet(date, true, "invoicesSentWave1");
  };

  const updateInvoicesWave2 = (date: Date | null) => {
    handleScheduleSet(date, true, "invoicesSentWave2");
  };

  const updateValueDate = (date: Date | null) => {
    handleScheduleSet(date, false, "valueDate");
  };

  useEffect(() => {
    // Prevent saving if timeline is invalid
    const {
      entriesApprovedByCustomer,
      entriesApprovedBySystem,
      invoicesSentWave1,
      invoicesSentWave2,
      salaryDate,
      end,
    } = schedule;
    let isValid = false;
    const salary = new Date(salaryDate).getTime();
    const endDate = new Date(end);
    endDate.setUTCHours(0, 0, 0, 0);
    const endTime = endDate.getTime();
    isValid =
      entriesApprovedByCustomer > endTime &&
      entriesApprovedByCustomer < entriesApprovedBySystem &&
      entriesApprovedBySystem < invoicesSentWave1 &&
      invoicesSentWave1 < invoicesSentWave2 &&
      salary > endTime;
    setIsFormValid(isValid);
  }, [schedule, scheduleDateRange]);

  // Note: This code assumes that we have two categories of scheduling class types
  // that have different approval timelines for payslips:
  //   1) once or twice per month (schc-1m + schc-2m),
  //   2) the others (schc-2w-odd + schc-2w-even)
  // if more scheduling class types are added this code needs to be updated
  const payslipApprovalTimelineRow = (type: string, row: SchedulingPeriod) => {
    const payslipsLocked =
      row.payslipsLocked &&
      "(" + format(row.payslipsLocked, dateTimeFormat) + ")";

    return (
      <TableRow>
        <TableCellGray>{t("payslipApproval")}</TableCellGray>
        <TableCell colSpan={1} />
        <TableCell />
        {type === "schc-1m" || type === "schc-2m" ? (
          <TableCell>
            {t("payslipsApproved")} <br />
            {t("hourBeforeInvoicesSent")}
          </TableCell>
        ) : (
          <TableCell>
            {t("payslipApprovalAndLock")} <br />
            {payslipsLocked || t("threeWDAfterPeriodEnd")}
          </TableCell>
        )}
        {type === "schc-1m" || type === "schc-2m" ? (
          <TableCell>
            {t("payslipsLocked")} <br />
            {payslipsLocked || t("hourAfterInvoicesSent")}
          </TableCell>
        ) : (
          <TableCell />
        )}
        <TableCell>{t("salaryValueDate")}</TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <HeaderBox
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{ cursor: "grab" }}
      >
        <h4>{t("schedulingPeriod")}</h4>
        {/* FIX?: this date picker handles GMT+2 times instead of standard time */}
        <DateRangePicker
          disabled={!isNewSchedulingPeriod}
          value={scheduleDateRange}
          onChange={updateDateRange}
        />
        <h4>{t("salaryDate")}</h4>
        <DatePicker
          label={null}
          value={new Date(schedule.salaryDate)}
          onChange={updateSalaryDate}
          slotProps={{
            textField: {
              error:
                new Date(schedule.salaryDate).getTime() <
                new Date(schedule.end).getTime(),
            },
          }}
        />
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </HeaderBox>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="spanning table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "lightgray" }}>
              <TableCell />
              <TableCell colSpan={7} sx={{ minWidth: "1500px" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCellGray />
              <TableCellConstantWidth>
                <DateTimePicker
                  ampm={false}
                  label={t("customerApproval")}
                  value={new Date(schedule.entriesApprovedByCustomer)}
                  onChange={updateCustomerApproval}
                  slotProps={{
                    textField: {
                      error:
                        schedule.entriesApprovedByCustomer <=
                        new Date(schedule.end).getTime(),
                    },
                  }}
                />
              </TableCellConstantWidth>
              <TableCellConstantWidth>
                <DateTimePicker
                  ampm={false}
                  label={t("systemApproval")}
                  value={new Date(schedule.entriesApprovedBySystem)}
                  onChange={updateSystemApproval}
                  slotProps={{
                    textField: {
                      error:
                        schedule.entriesApprovedBySystem <
                        schedule.entriesApprovedByCustomer,
                    },
                  }}
                />
              </TableCellConstantWidth>
              <TableCellConstantWidth>
                <DateTimePicker
                  ampm={false}
                  label={t("invoicesSentWave1")}
                  value={new Date(schedule.invoicesSentWave1)}
                  onChange={updateInvoicesWave1}
                  slotProps={{
                    textField: {
                      error:
                        schedule.invoicesSentWave1 <
                        schedule.entriesApprovedBySystem,
                    },
                  }}
                />
              </TableCellConstantWidth>
              <TableCellConstantWidth>
                <DateTimePicker
                  ampm={false}
                  label={t("invoicesSentWave2")}
                  value={new Date(schedule.invoicesSentWave2)}
                  onChange={updateInvoicesWave2}
                  slotProps={{
                    textField: {
                      error:
                        schedule.invoicesSentWave2 < schedule.invoicesSentWave1,
                    },
                  }}
                />
              </TableCellConstantWidth>
              <TableCellConstantWidth>
                <DatePicker
                  label={t("valueDate")}
                  value={new Date(schedule.valueDate)}
                  onChange={updateValueDate}
                />
              </TableCellConstantWidth>
            </TableRow>
            <TableRow>
              <TableCellGray>{t("hoursApproval")}</TableCellGray>
              <TableCell>{t("customerApproval")}</TableCell>
              <TableCell>{t("systemApproval")}</TableCell>
              <TableCell colSpan={3} />
            </TableRow>
            {payslipApprovalTimelineRow(type, schedule)}
            <TableRow>
              <TableCellGray>{t("billing")}</TableCellGray>
              <TableCell colSpan={2} />
              <TableCell>{t("invoicesSentWave1")}</TableCell>
              <TableCell>{t("invoicesSentWave2")}</TableCell>
              <TableCell>{t("invoiceValueDate")}</TableCell>
            </TableRow>
            <TableRow>
              <TableCellGray>{t("manualBilling")}</TableCellGray>
              <TableCell colSpan={2} />
              <TableCell />
              <TableCell>{t("markedAsReady")}</TableCell>
              <TableCell>{t("invoiceValueDate")}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <FormSaveCancelButtons
        onClose={onClose}
        onSubmit={() => onSave(schedule)}
        submitDisabled={!isFormValid}
      />
    </Box>
  );
};
